if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getCartState() {
        return fetch('/cart.js').then((response) => response.json())
      }

      reachCartLimit(byPrice = true) {
        const limitPopup = document.querySelector(".limit-popup")
        if (limitPopup) {
          limitPopup.setAttribute('aria-hidden', 'false')
          if(byPrice) {
            limitPopup.classList.add('amount-limit-reached')
          } else {
            limitPopup.classList.remove('amount-limit-reached')
          }
        }
      }

      async checkAndRemoveIfLimitsExceeded(variantId, cartLimits, quantity) {
        const cartState = await this.getCartState();
        const amountLimit = parseFloat(cartLimits.dataset.amount);
        const quantityLimit = parseInt(cartLimits.dataset.quantity);
        const currentAmount = cartState.total_price / 100;
        const currentQuantity = cartState.item_count;

        const amountExceeded = amountLimit > 0 && currentAmount > amountLimit;
        const quantityExceeded = quantityLimit > 0 && currentQuantity > quantityLimit;

        if (amountExceeded || quantityExceeded) {
          const addedItem = cartState.items.find((i) => i.id === Number(variantId));
          if (addedItem) {
            const lineIndex = cartState.items.indexOf(addedItem) + 1;
            await fetch(`${routes.cart_change_url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: lineIndex, quantity: addedItem.quantity - quantity })
            });
          }
          
          return { exceeded: true, amountExceeded };
        }
        
        return { exceeded: false, amountExceeded: false };
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        
        const cartLimits = document.querySelector('.cart-limits');
        let cartLimitToCheck = false;
        let amountExceeded = false;
        
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              
              this.handleErrorMessage(response.description);
              
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              
              if (!soldOutMessage) {
                const limitResult = await this.checkAndRemoveIfLimitsExceeded(formData.get('id'), cartLimits, formData.get('quantity'));
                if (limitResult.exceeded) {
                  cartLimitToCheck = true;
                  amountExceeded = limitResult.amountExceeded;
                }
                
                const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
                fetch(`/?sections=${sectionIds.join(',')}`)
                  .then(res => res.json())
                  .then(response => {
                    this.cart.renderContents({sections: response });
                  });
                return;
              }
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const limitResult = await this.checkAndRemoveIfLimitsExceeded(formData.get('id'), cartLimits, formData.get('quantity'));
            if (limitResult.exceeded) {
              cartLimitToCheck = true;
              amountExceeded = limitResult.amountExceeded;
              const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
              const updatedSections = await fetch(`/?sections=${sectionIds.join(',')}`).then(res => res.json());
              this.cart.renderContents({sections: updatedSections });
              
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            if (cartLimitToCheck) {
              setTimeout(() => {
                this.reachCartLimit(amountExceeded);
              }, 500);
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }

        setTimeout(() => {
          this.errorMessageWrapper.toggleAttribute('hidden', true);
        }, 3000);
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if(this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
